import { Tooltip } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil, Unlink } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AddButton, ModalConfirmation } from '@/components';
import { useHeroToast, useWarmup } from '@/hooks';
import { useOrganizationState } from '@/state';
import { useLocaleNavigate } from '@/utils/locale';
import { useGetAllRoomServices } from '@/views/RoomService/queries';

import {
  isKdsDeviceOnline, KdsDevice, kdsDevicesQueryKey, useGetKdsDevices, useUnpairKdsDevice,
} from '../../queries';
import { EditKdsDeviceModal } from '../EditKdsDeviceModal';
import * as S from './KdsDevicesSection.styles';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Lista ekranów KDS (tabletów kuchennych) hotelu wraz z parowaniem i odłączaniem.
 * Osobna sekcja od POD-ów, bo to inna klasa urządzeń: nie stoją w pokojach, nie
 * mają treści do edycji, a ich cały cykl życia to sparuj → pracuj → odłącz.
 */
export const KdsDevicesSection: FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useHeroToast();
  const { organizationId } = useOrganizationState();
  const navigate = useLocaleNavigate();

  useWarmup(['v3-listKdsDevices', 'v3-claimKdsDevice', 'v3-updateKdsDevice']);

  const [deviceToUnpair, setDeviceToUnpair] = useState<string | null>(null);
  const [deviceToEdit, setDeviceToEdit] = useState<KdsDevice | null>(null);

  const { data: devices = [], isLoading } = useGetKdsDevices(organizationId);
  const { data: roomServices = [], isSuccess: roomServicesLoaded } = useGetAllRoomServices(organizationId);
  const unpairDevice = useUnpairKdsDevice();

  const outletNameById = useMemo(
    () => new Map(roomServices.map((outlet) => [outlet.id, outlet.name])),
    [roomServices],
  );

  const refreshDevices = () => {
    queryClient.invalidateQueries({ queryKey: kdsDevicesQueryKey(organizationId) });
  };

  const formatLastSeen = (lastSeenAt: number | null): string => {
    if (!lastSeenAt) return t('myDevices.kds.neverSeen', 'nigdy');
    const elapsed = Date.now() - lastSeenAt;
    if (elapsed < MINUTE) return t('myDevices.kds.justNow', 'przed chwilą');
    if (elapsed < HOUR) {
      return t('myDevices.kds.minutesAgo', '{{count}} min temu', {
        count: Math.floor(elapsed / MINUTE),
      });
    }
    if (elapsed < DAY) {
      return t('myDevices.kds.hoursAgo', '{{count}} godz. temu', {
        count: Math.floor(elapsed / HOUR),
      });
    }
    return new Date(lastSeenAt).toLocaleString();
  };

  const renderStation = (device: KdsDevice) => {
    if (!device.gastronomyPointId) {
      return t('myDevices.kds.stationAll', 'Wszystkie stanowiska');
    }
    // Id bez skrótu = stanowisko usunięte w room service; tablet trzyma nieaktualne przypisanie.
    if (!device.gastronomyPoint) {
      return <S.StationFlag $tone="warning">{t('myDevices.kds.stationDeleted', 'Stanowisko usunięte')}</S.StationFlag>;
    }
    return (
      <S.StationCell>
        <S.StationName>
          <S.StationDot $color={device.gastronomyPoint.color} aria-hidden />
          <span>{device.gastronomyPoint.name}</span>
        </S.StationName>
        {!device.gastronomyPoint.isActive && (
          <S.StationFlag $tone="muted">{t('myDevices.kds.stationInactive', 'wyłączone')}</S.StationFlag>
        )}
      </S.StationCell>
    );
  };

  const tooltipClassNames = {
    base: 'pointer-events-auto',
    content: 'bg-white dark:bg-[#242a33] text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-[#374151] shadow-lg',
  };

  const handleUnpair = () => {
    if (!deviceToUnpair || !organizationId) return;
    unpairDevice.mutate(
      { hotelId: organizationId, deviceId: deviceToUnpair },
      {
        onSuccess: () => {
          showSuccess(t('myDevices.kds.unpairSuccess', 'Ekran został odłączony.'));
          setDeviceToUnpair(null);
          refreshDevices();
        },
        onError: () => {
          showError(t('myDevices.kds.errors.unpairFailed', 'Nie udało się odłączyć ekranu.'));
          setDeviceToUnpair(null);
        },
      },
    );
  };

  if (!organizationId) return null;

  return (
    <S.Section>
      <S.Header>
        <div>
          <S.Title>{t('myDevices.kds.sectionTitle', 'Ekrany KDS')}</S.Title>
          <S.Subtitle>
            {t(
              'myDevices.kds.sectionDescription',
              'Tablety w kuchni, na których obsługa widzi zamówienia room service i zmienia ich statusy.',
            )}
          </S.Subtitle>
        </div>
        <AddButton
          isSmall
          label={String(t('myDevices.kds.addDevice', 'Dodaj ekran'))}
          onClick={() => navigate('/settings/devices/kds/pair')}
        />
      </S.Header>

      <S.TableWrap>
        <S.Table>
          <thead>
            <tr>
              <th>{t('myDevices.kds.columnName', 'Nazwa')}</th>
              <th style={{ width: 200 }}>{t('myDevices.kds.columnOutlet', 'Punkt gastronomiczny')}</th>
              <th style={{ width: 200 }}>{t('myDevices.kds.columnStation', 'Stanowisko')}</th>
              <th style={{ width: 130 }}>{t('myDevices.kds.columnStatus', 'Status')}</th>
              <th style={{ width: 160 }}>{t('myDevices.kds.columnLastSeen', 'Ostatni kontakt')}</th>
              <th style={{ width: 96 }} />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="muted">
                  {t('common.loading', 'Ładowanie...')}
                </td>
              </tr>
            )}

            {!isLoading && devices.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 0 }}>
                  <S.EmptyContent>
                    <S.EmptyTitle>
                      {t('myDevices.kds.emptyTitle', 'Brak sparowanych ekranów KDS')}
                    </S.EmptyTitle>
                    <S.EmptyDescription>
                      {t(
                        'myDevices.kds.emptyDescription',
                        'Uruchom aplikację KDS na tablecie i przypisz go do hotelu kodem, który pokaże się na jego ekranie.',
                      )}
                    </S.EmptyDescription>
                  </S.EmptyContent>
                </td>
              </tr>
            )}

            {!isLoading && devices.map((device) => {
              const online = isKdsDeviceOnline(device.lastSeenAt);
              return (
                <tr key={device.deviceId}>
                  <td>
                    <S.DeviceName>
                      {device.name || t('myDevices.kds.unnamed', 'Ekran KDS')}
                    </S.DeviceName>
                    {device.model && <S.DeviceMeta>{device.model}</S.DeviceMeta>}
                  </td>
                  <td className="muted">
                    {device.roomServiceId
                      // Punkt usunięty w room service: nazwy już nie ma, nie pokazujemy surowego id.
                      ? outletNameById.get(device.roomServiceId)
                        ?? (roomServicesLoaded ? t('myDevices.kds.outletDeleted', 'Punkt usunięty') : '')
                      : t('myDevices.kds.outletAll', 'Wszystkie punkty')}
                  </td>
                  <td className={device.gastronomyPointId ? undefined : 'muted'}>{renderStation(device)}</td>
                  <td>
                    <S.StatusPill $online={online}>
                      {online
                        ? t('myDevices.kds.online', 'Online')
                        : t('myDevices.kds.offline', 'Offline')}
                    </S.StatusPill>
                  </td>
                  <td className="muted">{formatLastSeen(device.lastSeenAt)}</td>
                  <td className="right">
                    <S.RowActions>
                      <Tooltip
                        content={t('myDevices.kds.edit', 'Zmień punkt i stanowisko')}
                        showArrow
                        radius="sm"
                        size="sm"
                        delay={300}
                        classNames={tooltipClassNames}
                      >
                        <span>
                          <S.IconButton
                            type="button"
                            $tone="neutral"
                            onClick={() => setDeviceToEdit(device)}
                            aria-label={String(t('myDevices.kds.edit', 'Zmień punkt i stanowisko'))}
                          >
                            <Pencil />
                          </S.IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        content={t('myDevices.kds.unpair', 'Odłącz ekran')}
                        showArrow
                        radius="sm"
                        size="sm"
                        delay={300}
                        classNames={tooltipClassNames}
                      >
                        <span>
                          <S.IconButton
                            type="button"
                            onClick={() => setDeviceToUnpair(device.deviceId)}
                            aria-label={String(t('myDevices.kds.unpair', 'Odłącz ekran'))}
                          >
                            <Unlink />
                          </S.IconButton>
                        </span>
                      </Tooltip>
                    </S.RowActions>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </S.Table>
      </S.TableWrap>

      {deviceToEdit && (
        <EditKdsDeviceModal
          key={deviceToEdit.deviceId}
          hotelId={organizationId}
          device={deviceToEdit}
          roomServices={roomServices}
          onClose={() => setDeviceToEdit(null)}
        />
      )}

      <ModalConfirmation
        isOpen={!!deviceToUnpair}
        isLoading={unpairDevice.isPending}
        itemId={deviceToUnpair}
        onClose={() => setDeviceToUnpair(null)}
        onConfirm={handleUnpair}
        title={String(t('myDevices.kds.unpairTitle', 'Odłączyć ekran KDS?'))}
        description={t(
          'myDevices.kds.unpairDescription',
          'Tablet natychmiast straci dostęp do zamówień. Aby wrócić do pracy, będzie musiał sparować się od nowa.',
        )}
        buttonConfirm={String(t('myDevices.kds.unpair', 'Odłącz ekran'))}
      />
    </S.Section>
  );
};
